import Link from 'next/link';
import styled from 'styled-components';

import bluebonnetFrontpageImage from '../../../assets/images/bluebonetfontpage.png';
import bluebonnetPartnershipImage from '../../../assets/images/bluebonnetpartnership.png';
import closingBoxBluebonnetImage from '../../../assets/images/closingboxbluebonnet.jpeg';
import greenValleyBluebonnetImage from '../../../assets/images/greenvalleybluebonnet.png';
import dataGrowthImage from '../../../assets/images/growth2.png';
import pacingGuideBluebonnetImage from '../../../assets/images/pacingguidebluebonnet.png';
import phonicsBluebonnetImage from '../../../assets/images/phonicsbluebonnet.jpeg';
import sivellBendsBluebonnetImage from '../../../assets/images/sivellbendsbluebonnet.png';
import spanishBluebonnetImage from '../../../assets/images/spanish_bluebonnet.png';
import windOfTheWillowsImage from '../../../assets/images/windofthewillows.png';
import theme from '../../../assets/theme';
import { Button } from '../../_atoms/Button';
import MaxWidthContainer from '../../_atoms/MaxWidthContainer';

const MOBILE = '(max-width: 992px)';
const TABLET = '(max-width: 720px)';
const IMG_BASE = '/curriculum-bluebonnet';
const LEAD_TYPEFORM_URL = 'https://9uq31r57i3q.typeform.com/to/oyTyfiVe';
const BLUEBONNET_REQUEST_PATH = '/blue-bonnet-request';

export type BluebonnetVariant = 'default' | 'lead' | 'thank-you';

export interface BluebonnetLandingContentProps {
  variant?: BluebonnetVariant;
  isAuthenticated?: boolean;
}

const BluebonnetLandingContent: React.FC<BluebonnetLandingContentProps> = ({
  variant = 'default',
  isAuthenticated = false,
}) => {
  return (
    <>
      {/* ───────────────── 1. HERO ───────────────── */}
      <HeroSection>
        <HeroBlobOne aria-hidden="true" />
        <HeroBlobTwo aria-hidden="true" />
        <MaxWidthContainer>
          <HeroLayout>
            <HeroLeft>
              <HeroTitle>
                Bluebonnet is the curriculum.{' '}
                <HeroTitleAccent>
                  Curipod is the engagement and insights layer.
                </HeroTitleAccent>
              </HeroTitle>
              <HeroSubtitle>
                Student engagement. Rigorous writing practice. Clarity and insights
                district leaders need.
              </HeroSubtitle>
              {/* <HeroSubtitle>
                Student engagement. Rigorous writing practice. Clarity and insights district leaders need.
                Engagement teachers can feel, writing practice students love, and the
                clarity and insights district leaders need.
              </HeroSubtitle> */}
              <HeroCta variant={variant} />
            </HeroLeft>

            <HeroRight>
              <HeroImageFrame>
                <HeroImage
                  src={bluebonnetFrontpageImage.src}
                  alt="A Bluebonnet lesson running on a Curipod activity slide in a K-5 Texas classroom, students engaged, looking up at the screen."
                />
              </HeroImageFrame>
            </HeroRight>
          </HeroLayout>
        </MaxWidthContainer>
      </HeroSection>

      {/* ───────────── 2. STUDENTS ENGAGED — READ-ALOUD ───────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <InsightRow>
            <InsightLeft>
              <SectionTitle>
                Students Engaged and Writing During Every Read-Aloud
              </SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    AI-scored SCRs + ECRs, STAAR-aligned. No stack of papers to grade.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Built into the Bluebonnet read-aloud. No extra minutes, no extra prep.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Bluebonnet questions, feedback in seconds. Not next week.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
              <InsightCtaRow>
                <InsightCtaText>
                  Connect with our team to learn how Curipod maps into your Bluebonnet
                  scope &amp; sequence.
                </InsightCtaText>
                <Link href={BLUEBONNET_REQUEST_PATH} passHref>
                  <a>
                    <InsightCtaButton type="primary">Book a Demo</InsightCtaButton>
                  </a>
                </Link>
              </InsightCtaRow>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={windOfTheWillowsImage.src}
                alt="Curipod read-aloud SCR slide from The Wind in the Willows, Grade 3 Unit 1 Lesson 5."
                loading="lazy"
              />
              <InsightCaption>
                Grade 3 · Unit 1 · Lesson 5 — Read-Aloud from The Wind in the Willows
              </InsightCaption>
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 3. LANGUAGE, GRAMMAR, PHONICS PRACTICE ───────────── */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <InsightRow $reverse>
            <InsightLeft>
              <SectionTitle>Language, Grammar, and Phonics Practice (K-5)</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    The practice, feedback, and mastery loop students need before the
                    spiral moves on.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Fun, meaningful phonics practice where students want to write.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Sentence-level grammar practice applied in context, not drilled in
                    isolation.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={phonicsBluebonnetImage.src}
                alt="Curipod K-5 phonics writing activity."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 4. IMPLEMENTATION INSIGHTS ───────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <InsightRow>
            <InsightLeft>
              <SectionTitle>Insights for your Bluebonnet implementation</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    See where mastery is, and isn&apos;t.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>Spot the gap before STAAR does.</InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Built for Texas accountability alignment: HB 1605 &amp; HB 8.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
              <InsightCtaRow>
                <InsightCtaText>
                  Interested in seeing your district&apos;s dashboard?
                </InsightCtaText>
                <Link href={BLUEBONNET_REQUEST_PATH} passHref>
                  <a>
                    <InsightCtaButton type="primary">Contact our team</InsightCtaButton>
                  </a>
                </Link>
              </InsightCtaRow>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={dataGrowthImage.src}
                alt="District dashboard showing SCR and ECR score growth rolled up across schools and grades."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 5. BUILT FOR YOUR PACING GUIDE ───────────── */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <InsightRow $reverse>
            <InsightLeft>
              <SectionTitle>Built for Your Pacing Guide</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>
                    Questions, scaffolds, and student exemplars embedded in lessons.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Timed activities keep classes moving forward together.
                  </InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Feedback and revision cycles that used to take hours happen in
                    minutes.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={pacingGuideBluebonnetImage.src}
                alt="Curipod lesson aligned to the Bluebonnet pacing guide, showing timed activities and embedded scaffolds."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 6. EMERGENT BILINGUAL SUPPORT ───────────── */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <InsightRow>
            <InsightLeft>
              <SectionTitle>Emergent Bilingual Support</SectionTitle>
              <InsightBulletList>
                <InsightBullet>
                  <InsightBulletBody>Built-in translation support.</InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>Multilingual response options.</InsightBulletBody>
                </InsightBullet>
                <InsightBullet>
                  <InsightBulletBody>
                    Scaffolding built into each activity so every EB student participates.
                  </InsightBulletBody>
                </InsightBullet>
              </InsightBulletList>
            </InsightLeft>
            <InsightRight>
              <InsightImg
                src={spanishBluebonnetImage.src}
                alt="Curipod Bluebonnet activity rendered with Spanish translation and bilingual scaffolds."
                loading="lazy"
              />
            </InsightRight>
          </InsightRow>
        </MaxWidthContainer>
      </Section>

      {/* ───────────── 5. DICKINSON PARTNER SPOTLIGHT ───────────── */}
      <DickinsonSection id="dickinson-story">
        <MaxWidthContainer>
          <DickinsonHeader>
            <DickinsonTitle>
              How Dickinson ISD is bringing <AccentSpan>Bluebonnet to life.</AccentSpan>
            </DickinsonTitle>
            <DickinsonChipRow>
              <DickinsonChip>Dickinson ISD</DickinsonChip>
              <DickinsonChip>Region 4</DickinsonChip>
              <DickinsonChip>Galveston County, TX</DickinsonChip>
              <DickinsonChip>K-5 Bluebonnet RLA</DickinsonChip>
            </DickinsonChipRow>
          </DickinsonHeader>

          <DickinsonHeroImg
            src={bluebonnetPartnershipImage.src}
            alt="Curipod and Dickinson ISD literacy team partnering on the Bluebonnet K-5 RLA implementation."
            loading="lazy"
          />

          <DickinsonStory>
            <DickinsonChapterTag>The partnership</DickinsonChapterTag>
            <DickinsonBody>
              Dickinson ISD is adopting Bluebonnet Learning RLA for the 2026-27 school
              year, and they wanted the rollout to land with real energy in classrooms.
              Jennifer Brewer, Dickinson&apos;s K-12 Literacy Coordinator, and her
              literacy team knew the curriculum was strong, but they wanted something
              more: an engagement and insights layer that would make Bluebonnet come alive
              in classrooms. So she reached out to Curipod, already implemented at the
              high school level, to build it together.
            </DickinsonBody>
            <DickinsonBody>
              We jumped in side by side. The two teams sat down with the curriculum unit
              by unit. We met with teachers and coaches. We watched lessons unfold in
              dozens of real classrooms. And the opportunities came into focus fast:
              pacing, insights, writing practice with real-time feedback, grammar and
              skill practice, phonics, and Spanish support. We agreed on where Curipod
              could add the most value, and got to work.
            </DickinsonBody>

            <DickinsonChapterTag>What it looks like in Dickinson</DickinsonChapterTag>
            <DickinsonBody>
              From there, the Curipod team built lesson structures, alignments, and
              insights reporting. First for Dickinson teachers, and ultimately for
              teachers across Texas. Every activity was tested in multiple classrooms,
              with multiple teachers, until we knew it actually worked.
            </DickinsonBody>
            <DickinsonBody>
              And then the district saw what happens when Curipod is embedded inside
              Bluebonnet. Kids leaned in. Wrote more. Asked sharper questions. Stayed with
              hard ideas longer than anyone expected.
            </DickinsonBody>
            <DickinsonBody>
              Together with Dickinson, we&apos;ve built a growing library of Curipod
              activity formats: SCRs, ECRs, phonics, grammar, skill practice. Designed to
              fit the rhythm of every Bluebonnet K-5 lesson. Ready for the 2026-27 school
              year.
            </DickinsonBody>

            <DickinsonQuoteRow>
              <DickinsonQuotePortrait
                src={`${IMG_BASE}/image11.jpg`}
                alt="Jennifer Brewer, K-12 Literacy Coordinator at Dickinson ISD"
                loading="lazy"
              />
              <DickinsonPullQuote $variant="highlight">
                <DickinsonPullQuoteText>
                  What I love about working with Curipod is they don&apos;t hand you a
                  finished product. They sit down with you, and you build it together.{' '}
                  <em>
                    My teachers feel ownership of it, and I know that means it will make a
                    real impact.
                  </em>
                </DickinsonPullQuoteText>
                <DickinsonPullQuoteAttrib>
                  Jennifer Brewer, K-12 Literacy Coordinator, Dickinson ISD
                </DickinsonPullQuoteAttrib>
              </DickinsonPullQuote>
            </DickinsonQuoteRow>
          </DickinsonStory>
        </MaxWidthContainer>
      </DickinsonSection>

      {/* ───────────── 6. STAAR PROOF BAND ───────────── */}
      <ProofSection>
        <MaxWidthContainer>
          <NarrowCenter>
            <ProofTitle>
              Real STAAR gains across Texas — now mapped to every Bluebonnet lesson.
            </ProofTitle>
            <ProofSubtitle>
              Districts already use Curipod to move STAAR scores, campus ratings, and
              passing rates. These are results your Bluebonnet implementation can build
              on.
            </ProofSubtitle>
          </NarrowCenter>

          <FeaturedStoryGrid>
            <Link href="/case-studies/curipod-case-study-green-valley" passHref>
              <FeaturedStoryCard>
                <FeaturedStoryImage
                  src={greenValleyBluebonnetImage.src}
                  alt="Green Valley Elementary students using Curipod with the Bluebonnet curriculum."
                  loading="lazy"
                />
                <FeaturedStoryBody>
                  <FeaturedStoryStat>69% → 89%</FeaturedStoryStat>
                  <FeaturedStoryTitle>
                    Green Valley&apos;s Third-Grade Growth Story.
                  </FeaturedStoryTitle>
                  <FeaturedStoryMeta>
                    Region 20 · Schertz-Cibolo-Universal City ISD · 2024–2025 School Year
                  </FeaturedStoryMeta>
                  <FeaturedStoryLink>Read the story →</FeaturedStoryLink>
                </FeaturedStoryBody>
              </FeaturedStoryCard>
            </Link>
            <Link href="/case-studies/curipod-case-study-sivells-bend" passHref>
              <FeaturedStoryCard>
                <FeaturedStoryImage
                  src={sivellBendsBluebonnetImage.src}
                  alt="Sivells Bend ISD classroom showcasing a Curipod activity."
                  loading="lazy"
                />
                <FeaturedStoryBody>
                  <FeaturedStoryStat>C → A</FeaturedStoryStat>
                  <FeaturedStoryTitle>
                    Sivells Bend ISD: A campus rating in one year.
                  </FeaturedStoryTitle>
                  <FeaturedStoryMeta>Sivells Bend ISD · North Texas</FeaturedStoryMeta>
                  <FeaturedStoryLink>Read the story →</FeaturedStoryLink>
                </FeaturedStoryBody>
              </FeaturedStoryCard>
            </Link>
          </FeaturedStoryGrid>

          <StatStrip>
            <Link href="/case-studies/curipod-case-study-south-texas" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +22<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Meets &amp; Masters on STAAR</StatCardLabel>
                  <StatCardSub>South Texas</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-south-east-texas" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +32<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Growth in STAAR scores</StatCardLabel>
                  <StatCardSub>South East Texas</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-ysleta" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +43<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>A 4th-grade transformation</StatCardLabel>
                  <StatCardSub>Corpus Christi, TX</StatCardSub>
                </StatCard>
              </a>
            </Link>
            <Link href="/case-studies/curipod-case-study-flour-bluff" passHref>
              <a>
                <StatCard>
                  <StatBigNumber>
                    +20<StatBigPct>%</StatBigPct>
                  </StatBigNumber>
                  <StatCardLabel>Increase in STAAR RLA Passing Rate</StatCardLabel>
                  <StatCardSub>Schertz-Cibolo-Universal City ISD</StatCardSub>
                </StatCard>
              </a>
            </Link>
          </StatStrip>

          <ProofAllStoriesLinkRow>
            <Link href="/case-studies" passHref>
              <ProofAllStoriesLink>See all the case studies →</ProofAllStoriesLink>
            </Link>
          </ProofAllStoriesLinkRow>
        </MaxWidthContainer>
      </ProofSection>

      {/* ───────────── 12. CLOSING CTA ───────────── */}
      <FinalCtaWrapper>
        <MaxWidthContainer>
          <FinalCtaCard>
            <FinalCtaImageWrap>
              <FinalCtaImage
                src={closingBoxBluebonnetImage.src}
                alt="Curipod team working with Texas literacy educators on the Bluebonnet implementation."
                loading="lazy"
              />
            </FinalCtaImageWrap>
            <FinalCtaContent>
              <FinalCtaBody>
                Meet with us to learn how we can support you with your Bluebonnet
                implementation.
              </FinalCtaBody>
              <Link href={BLUEBONNET_REQUEST_PATH} passHref>
                <a>
                  <FinalCtaPrimaryButton type="primary">
                    Book a Call
                  </FinalCtaPrimaryButton>
                </a>
              </Link>
            </FinalCtaContent>
          </FinalCtaCard>
        </MaxWidthContainer>
      </FinalCtaWrapper>
    </>
  );
};

export default BluebonnetLandingContent;

/* ─── HERO CTA (varies by variant) ────────────────────────────────────────── */

interface HeroCtaProps {
  variant: BluebonnetVariant;
}

const HeroCta: React.FC<HeroCtaProps> = ({ variant }) => {
  if (variant === 'lead') {
    return (
      <HeroCtaRow>
        <HeroPrimaryButtonLink href={LEAD_TYPEFORM_URL}>
          <HeroPrimaryButton type="primary">Book a demo</HeroPrimaryButton>
        </HeroPrimaryButtonLink>
        <HeroSecondaryLink href="#dickinson-story">
          See the Dickinson Story →
        </HeroSecondaryLink>
      </HeroCtaRow>
    );
  }

  if (variant === 'thank-you') {
    return (
      <HeroThankYouCard role="status" aria-live="polite">
        <HeroThankYouTitle>Thanks — we got it.</HeroThankYouTitle>
        <HeroThankYouBody>
          Someone from the Curipod team will be in touch shortly with more information
          about Curipod + Bluebonnet for your classroom or district.
        </HeroThankYouBody>
      </HeroThankYouCard>
    );
  }

  return (
    <HeroCtaRow>
      <Link href={BLUEBONNET_REQUEST_PATH} passHref>
        <a>
          <HeroPrimaryButton type="primary">Book a Demo</HeroPrimaryButton>
        </a>
      </Link>
      <HeroSecondaryLink href="#dickinson-story">
        See District Success Story →
      </HeroSecondaryLink>
    </HeroCtaRow>
  );
};

/* ─── 1. HERO ─────────────────────────────────────────────────────────────── */

const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  color: ${theme.colors.white};
  padding: 100px 20px 90px;
  overflow: hidden;

  @media ${MOBILE} {
    padding: 80px 16px 64px;
  }
`;

const HeroBlobOne = styled.div`
  position: absolute;
  top: -180px;
  right: -140px;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
`;

const HeroBlobTwo = styled.div`
  position: absolute;
  bottom: -120px;
  left: -100px;
  width: 460px;
  height: 460px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  pointer-events: none;
`;

const HeroLayout = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 56px;
  align-items: center;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroLeft = styled.div`
  position: relative;
`;

const HeroRight = styled.div`
  position: relative;

  @media ${MOBILE} {
    order: -1;
  }
`;

const HeroImageFrame = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.25), 0 0 0 6px rgba(255, 255, 255, 0.18);
  background: ${theme.colors.white};
  aspect-ratio: 4 / 3;

  @media ${MOBILE} {
    aspect-ratio: 3 / 2;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroTitle = styled.h1`
  font-size: 54px;
  line-height: 1.08;
  font-weight: 900;
  color: ${theme.colors.white};
  margin: 0 0 20px;
  letter-spacing: -1.2px;

  @media (max-width: 1200px) {
    font-size: 46px;
  }

  @media ${MOBILE} {
    font-size: 38px;
  }

  @media (max-width: 420px) {
    font-size: 30px;
  }
`;

const HeroTitleAccent = styled.span`
  display: block;
  font-weight: 800;
  color: #fff3e0;
  letter-spacing: -0.8px;
  margin-top: 6px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.92);
  max-width: 640px;
  line-height: 1.65;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 16px;
  }
`;

const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
`;

const HeroPrimaryButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 36px;
  font-size: 17px;
  height: 56px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.32);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }
`;

const HeroSecondaryLink = styled.a`
  color: ${theme.colors.white};
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.5);
  padding-bottom: 2px;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-bottom-color: ${theme.colors.white};
    transform: translateX(2px);
  }
`;

const HeroPrimaryButtonLink = styled.a`
  display: inline-block;
  text-decoration: none;
`;

const HeroThankYouCard = styled.div`
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 20px;
  padding: 24px 28px;
  max-width: 560px;
  color: ${theme.colors.white};
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
`;

const HeroThankYouTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  margin: 0 0 8px;
  color: ${theme.colors.white};
  letter-spacing: -0.3px;
`;

const HeroThankYouBody = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
`;

/* ─── SHARED SECTION PRIMITIVES ────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 90px 0;

  @media ${MOBILE} {
    padding: 56px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2.2px;
  margin-bottom: 14px;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 22px;
    height: 2px;
    background: ${theme.colors.orangeDark};
    border-radius: 2px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 22px;
  line-height: 1.15;
  letter-spacing: -0.6px;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  margin: 0 0 18px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const AccentSpan = styled.span`
  color: ${theme.colors.orange};
`;

const NarrowCenter = styled.div`
  max-width: 820px;
  margin: 0 auto 56px;
  text-align: left;

  @media ${MOBILE} {
    margin-bottom: 36px;
  }
`;

/* ─── PLACEHOLDER BOX (reusable) ──────────────────────────────────────────── */

const PlaceholderBox = styled.div<{
  $aspectRatio?: string;
  $size?: 'sm' | 'md' | 'lg';
}>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || '4 / 3'};
  background: ${theme.colors.paleBeige};
  border: 2px dashed rgba(232, 123, 39, 0.55);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ $size }) => ($size === 'lg' ? '32px' : $size === 'sm' ? '12px' : '20px')};
  color: ${theme.colors.orangeDark};
  text-align: center;
`;

const PlaceholderIcon = styled.div`
  font-size: 28px;
  line-height: 1;
`;

const PlaceholderLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
`;

const PlaceholderHint = styled.div`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  opacity: 0.7;
`;

/* ─── 3. IMPLEMENTATION INSIGHTS ─────────────────────────────────────────── */

const InsightRow = styled.div<{ $reverse?: boolean }>`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 56px;
  align-items: center;
  direction: ${({ $reverse }) => ($reverse ? 'rtl' : 'ltr')};

  & > * {
    direction: ltr;
  }

  @media ${MOBILE} {
    direction: ltr;
    grid-template-columns: 1fr;
    gap: 36px;

    /* On mobile always stack text first, image second. */
    & > :first-child {
      order: 1;
    }
    & > :nth-child(2) {
      order: 2;
    }
  }
`;

const InsightCaption = styled.div`
  margin-top: 14px;
  font-size: 13px;
  font-weight: 700;
  font-style: italic;
  color: ${theme.colors.text.dark};
  line-height: 1.45;
`;

const InsightCtaRow = styled.div`
  margin-top: 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
`;

const InsightCtaText = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0;
  flex: 1 1 auto;
  line-height: 1.5;
`;

const InsightCtaButton = styled(Button)`
  background-color: ${theme.colors.orange};
  color: ${theme.colors.white};
  border-radius: 28px;
  padding: 0 26px;
  font-size: 14px;
  height: 44px;
  font-weight: 800;
  white-space: nowrap;

  &:hover {
    background-color: ${theme.colors.orangeDark};
    color: ${theme.colors.white};
  }
`;

const InsightLeft = styled.div``;

const InsightRight = styled.div``;

const InsightImgFrame = styled.div`
  background: rgb(250, 243, 234);
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(232, 123, 39, 0.18);
  box-shadow: 0 16px 38px rgba(26, 21, 48, 0.08);
`;

const InsightImgInner = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 10px;
  object-fit: contain;
`;

const InsightImg: React.FC<React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
}> = props => (
  <InsightImgFrame>
    <InsightImgInner {...props} />
  </InsightImgFrame>
);

const InsightBulletList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InsightBullet = styled.li`
  position: relative;
  padding-left: 28px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${theme.colors.orange};
  }
`;

const InsightBulletBody = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.black};
  line-height: 1.55;
  margin: 0;
`;

/* ─── 4. A CLOSER LOOK (LESSON EXAMPLES) ─────────────────────────────────── */

const LessonExamplesStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;

  @media ${MOBILE} {
    gap: 36px;
  }
`;

const LessonExampleRow = styled.div<{ $reverse: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 40px;
  align-items: center;
  direction: ${({ $reverse }) => ($reverse ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media ${MOBILE} {
    direction: ltr;
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const LessonExampleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LessonExampleTitle = styled.h3`
  font-size: 26px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0;
  line-height: 1.2;

  em {
    font-style: italic;
    color: ${theme.colors.orangeDark};
  }
`;

const LessonExampleMeta = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.orangeDark};
  letter-spacing: 1.4px;
  text-transform: uppercase;
  margin: 0;
`;

const LessonExampleKicker = styled.p`
  font-size: 17px;
  color: ${theme.colors.text.dark};
  margin: 0;
  line-height: 1.6;
`;

const LessonExampleImage = styled.div`
  background: rgb(243, 235, 218);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 16px 38px rgba(26, 21, 48, 0.08);
  border: 1px solid rgba(232, 123, 39, 0.18);

  @media ${MOBILE} {
    padding: 16px;
  }
`;

const LessonExampleImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  background: ${theme.colors.white};
  object-fit: contain;
`;

/* ─── 5. ENGAGEMENT MOMENT GRID ──────────────────────────────────────────── */

const EngagementMomentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const EngagementMomentCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 10px;
  padding: 24px;
  border: 1px solid rgba(232, 123, 39, 0.18);
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(26, 21, 48, 0.08);
  }
`;

const CuripodSlidePlaceholder = styled.div`
  background: ${theme.colors.lightOrangeBg};
  border: 2px dashed rgba(232, 123, 39, 0.55);
  border-radius: 10px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 140px;
  text-align: center;
`;

const CuripodSlideTag = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
  background: ${theme.colors.white};
  border: 1px solid rgba(232, 123, 39, 0.35);
  padding: 5px 12px;
  border-radius: 20px;
`;

const CuripodSlideLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.black};
  line-height: 1.4;
`;

const MomentTag = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: ${theme.colors.orangeDark};
  background: ${theme.colors.lightOrangeBg};
  padding: 5px 10px;
  border-radius: 20px;
  align-self: flex-start;
`;

const MomentTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0;
  line-height: 1.25;
`;

const MomentBody = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0;
`;

/* ─── 6. COMPARISON TABLE ────────────────────────────────────────────────── */

const ComparisonTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  -webkit-overflow-scrolling: touch;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 640px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(40, 47, 51, 0.12);
`;

const ComparisonTh = styled.th<{ $highlight?: boolean }>`
  text-align: left;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  padding: 18px 22px;
  background: ${({ $highlight }) =>
    $highlight ? theme.colors.orange : theme.colors.black};
  color: ${theme.colors.white};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ComparisonTd = styled.td<{ $highlight?: boolean; $bold?: boolean }>`
  font-size: 15px;
  line-height: 1.55;
  padding: 18px 22px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(249, 151, 70, 0.10)' : theme.colors.white};
  color: ${theme.colors.text.dark};
  border-top: 1px solid rgba(40, 47, 51, 0.06);
  vertical-align: top;
  font-weight: ${({ $bold }) => ($bold ? 700 : 400)};
  ${({ $bold }) =>
    $bold &&
    `
    color: #1a1a1a;
  `}

  tr:first-child & {
    border-top: none;
  }
`;

/* ─── 7. THREE PILLARS ───────────────────────────────────────────────────── */

const PillarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const PillarCard = styled.div`
  background: ${theme.colors.paleBeige};
  border-radius: 10px;
  padding: 32px 28px;
  border: 1px solid rgba(232, 123, 39, 0.14);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(26, 21, 48, 0.08);
  }
`;

const PillarNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const PillarTitle = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 14px;
  line-height: 1.22;
`;

const PillarBody = styled.p`
  font-size: 15px;
  line-height: 1.65;
  color: ${theme.colors.text.dark};
  margin: 0 0 12px;
`;

const PillarFootnote = styled.p`
  font-size: 13px;
  font-style: italic;
  color: ${theme.colors.text.dark};
  opacity: 0.85;
  margin: 0 0 22px;
`;

const PillarImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  display: block;
  margin-top: auto;
  background: ${theme.colors.white};
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 1px solid rgba(232, 123, 39, 0.18);
`;

const PillarImageStack = styled.div`
  margin-top: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  ${PillarImage} {
    aspect-ratio: 1 / 1;
    margin-top: 0;
  }
`;

/* ─── 8. DICKINSON SPOTLIGHT ─────────────────────────────────────────────── */

const DickinsonSection = styled.section`
  background: linear-gradient(
    180deg,
    ${theme.colors.paleBeige} 0%,
    ${theme.colors.background} 100%
  );
  padding: 100px 0;

  @media ${MOBILE} {
    padding: 64px 0;
  }
`;

const DickinsonTitle = styled.h2`
  font-size: 44px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin: 0 0 18px;
  line-height: 1.15;
  letter-spacing: -0.5px;

  @media ${MOBILE} {
    font-size: 32px;
  }
`;

const DickinsonChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const DickinsonChip = styled.span`
  background: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border: 1px solid rgba(232, 123, 39, 0.35);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 20px;
`;

const DickinsonHeader = styled.div`
  text-align: center;
  max-width: 820px;
  margin: 0 auto 40px;

  ${SectionTag} {
    justify-content: center;
  }

  @media ${MOBILE} {
    margin-bottom: 28px;
  }
`;

const DickinsonHeroImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 16px;
  background: ${theme.colors.white};
  box-shadow: 0 20px 50px rgba(26, 21, 48, 0.14);
  border: 1px solid rgba(232, 123, 39, 0.18);
  margin-bottom: 48px;
  object-fit: cover;

  @media ${MOBILE} {
    margin-bottom: 32px;
    border-radius: 12px;
  }
`;

const DickinsonStory = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const DickinsonChapterTag = styled.h3`
  font-size: 13px;
  font-weight: 800;
  color: ${theme.colors.orange};
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 32px 0 14px;

  &:first-child {
    margin-top: 0;
  }
`;

const DickinsonBody = styled.p`
  font-size: 17px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  margin: 0 0 18px;

  @media ${MOBILE} {
    font-size: 16px;
  }

  em {
    font-style: italic;
    color: ${theme.colors.black};
    font-weight: 600;
  }
`;

const DickinsonQuoteRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 28px;
  align-items: center;
  margin-top: 28px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 18px;
    justify-items: center;
  }
`;

const DickinsonQuotePortrait = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 50%;
  background: ${theme.colors.white};
  box-shadow: 0 12px 28px rgba(26, 21, 48, 0.12);
  border: 4px solid ${theme.colors.white};
  display: block;

  @media ${MOBILE} {
    max-width: 160px;
  }
`;

const DickinsonPullQuote = styled.blockquote<{ $variant?: 'highlight' }>`
  position: relative;
  margin: 0;
  padding: 32px 36px 28px;
  border-radius: 16px;
  background: ${({ $variant }) =>
    $variant === 'highlight' ? theme.colors.orange : theme.colors.white};
  color: ${({ $variant }) =>
    $variant === 'highlight' ? theme.colors.white : theme.colors.black};
  box-shadow: 0 16px 40px rgba(26, 21, 48, 0.08);
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'highlight' ? 'rgba(255,255,255,0.25)' : 'rgba(232, 123, 39, 0.2)'};

  @media ${MOBILE} {
    padding: 26px 22px 22px;
  }
`;

const DickinsonPullQuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 18px;

  em {
    font-style: italic;
    font-weight: 600;
  }

  @media ${MOBILE} {
    font-size: 19px;
  }
`;

const DickinsonPullQuoteAttrib = styled.cite`
  display: block;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.5px;
  opacity: 0.9;
`;

const DickinsonPortrait = styled.img`
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  border-radius: 16px;
  background: ${theme.colors.white};
  box-shadow: 0 16px 40px rgba(26, 21, 48, 0.12);
  border: 1px solid rgba(232, 123, 39, 0.2);
  display: block;
`;

/* ─── 9. PROOF / STATS STRIP ─────────────────────────────────────────────── */

const ProofSection = styled.section`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  padding: 100px 0;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 64px 0;
  }
`;

const ProofTitle = styled(SectionTitle)`
  color: ${theme.colors.white};
  margin-bottom: 16px;

  @media (max-width: 1200px) {
    font-size: 36px;
  }
`;

const ProofSubtitle = styled.p`
  font-size: 17px;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.6;
  max-width: 760px;
  margin: 0 0 48px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const FeaturedStoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  margin: 0 auto 36px;
  max-width: 880px;
  justify-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 460px;
  }

  a {
    text-decoration: none;
    color: inherit;
    display: block;
  }
`;

const FeaturedStoryCard = styled.a`
  display: block;
  background: ${theme.colors.white};
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  overflow: hidden;
  color: ${theme.colors.black};
  text-decoration: none;
  box-shadow: 0 18px 38px rgba(26, 21, 48, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 48px rgba(26, 21, 48, 0.24);
  }
`;

const FeaturedStoryImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: ${theme.colors.paleBeige};
`;

const FeaturedStoryBody = styled.div`
  padding: 26px 28px 28px;
`;

const FeaturedStoryStat = styled.div`
  font-size: 38px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
  line-height: 1;
  letter-spacing: -1px;
  margin-bottom: 12px;
`;

const FeaturedStoryTitle = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin-bottom: 10px;
  line-height: 1.3;
`;

const FeaturedStoryMeta = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  letter-spacing: 0.4px;
  margin-bottom: 18px;
  line-height: 1.5;
`;

const FeaturedStoryLink = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  letter-spacing: 1.2px;
`;

const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 880px;
  margin: 0 auto;
  justify-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 520px;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    max-width: 320px;
  }

  a {
    text-decoration: none;
    color: inherit;
    display: block;
  }
`;

const StatCard = styled.div`
  padding: 24px 14px;
  border-radius: 10px;
  text-align: center;
  height: 100%;
  width: 100%;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.32);

  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.26);
  }
`;

const StatBigNumber = styled.div`
  font-size: 40px;
  font-weight: 900;
  line-height: 1;
  color: ${theme.colors.white};
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  letter-spacing: -1px;

  @media ${MOBILE} {
    font-size: 34px;
  }
`;

const StatBigPct = styled.span`
  font-size: 22px;
  font-weight: 800;
`;

const StatArrow = styled.span`
  font-size: 26px;
  margin: 0 4px;
  opacity: 0.85;
`;

const StatCardLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: ${theme.colors.white};
  margin-top: 12px;
  line-height: 1.35;
`;

const StatCardSub = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
`;

const ProofAllStoriesLinkRow = styled.div`
  margin-top: 36px;
  text-align: center;
`;

const ProofAllStoriesLink = styled.a`
  display: inline-block;
  color: ${theme.colors.white};
  font-weight: 800;
  font-size: 15px;
  text-decoration: none;
  border-bottom: 2px solid rgba(255, 255, 255, 0.55);
  padding-bottom: 4px;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-bottom-color: ${theme.colors.white};
    transform: translateX(2px);
  }
`;

/* ─── 12. CLOSING CTA ────────────────────────────────────────────────────── */

const FinalCtaWrapper = styled.section`
  background: ${theme.colors.background};
  padding: 64px 0 80px;

  @media ${MOBILE} {
    padding: 48px 0 56px;
  }
`;

const FinalCtaCard = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  border-radius: 20px;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: stretch;
  color: ${theme.colors.white};
  box-shadow: 0 24px 50px rgba(232, 123, 39, 0.25);
  overflow: hidden;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const FinalCtaImageWrap = styled.div`
  position: relative;
  min-height: 280px;
  background: ${theme.colors.paleBeige};
`;

const FinalCtaImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  min-height: 280px;
  object-fit: cover;
`;

const FinalCtaContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  padding: 48px 56px;

  @media ${MOBILE} {
    padding: 32px 28px 36px;
    gap: 20px;
  }
`;

const FinalCtaBody = styled.p`
  font-size: 24px;
  font-weight: 800;
  color: ${theme.colors.white};
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.3px;

  @media ${MOBILE} {
    font-size: 20px;
  }
`;

const FinalCtaPrimaryButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 32px;
  font-size: 16px;
  height: 50px;
  font-weight: 800;
  align-self: flex-start;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
  }
`;
